import { Injectable, inject } from '@angular/core';
import { Firestore, collection, query, where, getDocs, addDoc, onSnapshot } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, BehaviorSubject } from 'rxjs';
import { map, filter, take } from 'rxjs/operators';
import { AuthService } from './auth.service';

export interface StripeProduct {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  metadata?: { [key: string]: string };
  prices: StripePrice[];
}

export interface StripePrice {
  id: string;
  product: string;
  active: boolean;
  currency: string;
  type: 'one_time' | 'recurring';
  unit_amount: number;
  recurring?: {
    interval: 'day' | 'week' | 'month' | 'year';
    interval_count: number;
  };
  metadata?: { [key: string]: string };
}

export interface CheckoutSession {
  id?: string;
  price: string;
  success_url: string;
  cancel_url: string;
  automatic_tax?: boolean;
  tax_id_collection?: boolean;
  trial_period_days?: number;
  url?: string;
  error?: { message: string };
}

export interface StripeSubscription {
  id: string;
  status: 'active' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'unpaid' | 'trialing';
  cancel_at_period_end: boolean;
  current_period_start: number;
  current_period_end: number;
  product: StripeProduct;
  price: StripePrice;
  quantity?: number;
  trial_end?: number;
  trial_start?: number;
  metadata?: { [key: string]: string };
}

export interface PortalSession {
  id?: string;
  return_url: string;
  url?: string;
  error?: { message: string };
}

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private authService = inject(AuthService);
  
  private productsSubject = new BehaviorSubject<StripeProduct[]>([]);
  public products$ = this.productsSubject.asObservable();

  // Initialize callable function in injection context
  private createPortalLinkCallable = httpsCallable(this.functions, 'ext-firestore-stripe-payments-createPortalLink');

  constructor() {
    this.loadProducts();
  }

  /**
   * Load all active products and their prices from Firebase
   */
  private async loadProducts(): Promise<void> {
    try {
      const productsCollection = collection(this.firestore, 'products');
      const productsQuery = query(productsCollection, where('active', '==', true));
      
      const querySnapshot = await getDocs(productsQuery);
      const products: StripeProduct[] = [];

      for (const docSnapshot of querySnapshot.docs) {
        const productData = { id: docSnapshot.id, ...docSnapshot.data() } as StripeProduct;
        
        // Fetch prices for this product
        const pricesCollection = collection(docSnapshot.ref, 'prices');
        const pricesSnapshot = await getDocs(pricesCollection);
        
        productData.prices = pricesSnapshot.docs.map(priceDoc => ({
          id: priceDoc.id,
          ...priceDoc.data()
        })) as StripePrice[];

        products.push(productData);
      }

      this.productsSubject.next(products);
    } catch (error) {
      console.error('Error loading products:', error);
      this.productsSubject.next([]);
    }
  }

  /**
   * Get products by name or metadata
   */
  getProductByName(name: string): Observable<StripeProduct | undefined> {
    return this.products$.pipe(
      map(products => products.find(product => 
        product.name.toLowerCase().includes(name.toLowerCase())
      ))
    );
  }

  /**
   * Get a specific price by interval (monthly/yearly)
   */
  getPriceByInterval(product: StripeProduct, interval: 'month' | 'year'): StripePrice | undefined {
    return product.prices.find(price => 
      price.active && 
      price.type === 'recurring' && 
      price.recurring?.interval === interval
    );
  }

  /**
   * Create a checkout session for subscription
   * @param priceId - The Stripe price ID
   * @param successUrl - URL to redirect on successful checkout
   * @param cancelUrl - URL to redirect on cancelled checkout
   * @param currentUser - The authenticated user
   * @param trialPeriodDays - Optional trial period in days (default: 30 for standard subscriptions)
   */
  async createCheckoutSession(
    priceId: string, 
    successUrl?: string, 
    cancelUrl?: string,
    currentUser?: any,
    trialPeriodDays?: number
  ): Promise<Observable<CheckoutSession>> {
    if (!currentUser) {
      throw new Error('User must be authenticated to create checkout session');
    }

    const checkoutData: Omit<CheckoutSession, 'id'> = {
      price: priceId,
      success_url: successUrl || `${window.location.origin}/pricing?success=true`,
      cancel_url: cancelUrl || `${window.location.origin}/pricing?canceled=true`,
      automatic_tax: true,
      tax_id_collection: true,
      ...(trialPeriodDays !== undefined && { trial_period_days: trialPeriodDays })
    };

    try {
      const docRef = await addDoc(
        collection(this.firestore, 'customers', currentUser.uid, 'checkout_sessions'),
        checkoutData
      );

      return new Observable<CheckoutSession>(observer => {
        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data() as CheckoutSession;
          
          if (data?.error) {
            observer.error(new Error(`Checkout session error: ${data.error.message}`));
            unsubscribe();
            return;
          }
          
          if (data?.url) {
            observer.next({ id: snap.id, ...data });
            observer.complete();
            unsubscribe();
            return;
          }
          
          // Still waiting for the session to be created
          observer.next({ id: snap.id, ...data });
        });

        return () => unsubscribe();
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Redirect to Stripe Checkout
   */
  redirectToCheckout(checkoutUrl: string): void {
    window.location.assign(checkoutUrl);
  }

  /**
   * Format price for display
   */
  formatPrice(price: StripePrice | null | undefined): string {
    if (!price || !price.unit_amount) {
      return 'N/A';
    }

    const amount = price.unit_amount / 100;
    const currency = (price.currency || 'usd').toUpperCase();
    
    if (price.type === 'recurring' && price.recurring) {
      const interval = price.recurring.interval || 'month';
      const intervalCount = price.recurring.interval_count || 1;
      
      let intervalText: string = interval;
      if (intervalCount > 1) {
        intervalText = `${intervalCount} ${interval}s`;
      }
      
      return `$${amount.toFixed(0)}/${intervalText}`;
    }
    
    return `$${amount.toFixed(2)} ${currency}`;
  }

  /**
   * Calculate savings percentage for annual vs monthly pricing
   */
  calculateSavings(monthlyPrice: StripePrice, annualPrice: StripePrice): number {
    if (!monthlyPrice || !annualPrice) return 0;
    
    const monthlyAnnualCost = monthlyPrice.unit_amount * 12;
    const annualCost = annualPrice.unit_amount;
    const savings = ((monthlyAnnualCost - annualCost) / monthlyAnnualCost) * 100;
    
    return Math.round(savings);
  }

  /**
   * Get current user subscriptions from Firebase using onSnapshot
   * Following the official Firebase Extensions for Stripe Payments pattern
   */
  getCurrentUserSubscriptions(currentUser: any): Observable<StripeSubscription[]> {
    if (!currentUser) {
      throw new Error('User must be authenticated to get subscriptions');
    }

    return new Observable<StripeSubscription[]>(observer => {
      let snapshotUnsubscribe: (() => void) | null = null;

      const productsSubscription = this.productsSubject
        .pipe(filter(products => products.length > 0), take(1))
        .subscribe(() => {
          const subscriptionsQuery = query(
            collection(this.firestore, "customers", currentUser.uid, "subscriptions"),
            where("status", "in", ["trialing", "active"])
          );

          snapshotUnsubscribe = onSnapshot(subscriptionsQuery, (snapshot) => {
            const subscriptions: StripeSubscription[] = [];

            snapshot.docs.forEach(doc => {
              const subscriptionData = { id: doc.id, ...doc.data() } as any;

              let product: StripeProduct | undefined;
              let price: StripePrice | undefined;

              if (subscriptionData.price?.product) {
                product = this.productsSubject.value.find(p => p.id === subscriptionData.price.product);
                price = subscriptionData.price;
              } else if (subscriptionData.items && subscriptionData.items.length > 0) {
                const firstItem = subscriptionData.items[0];
                if (firstItem.price) {
                  price = firstItem.price;
                  product = this.productsSubject.value.find(p => p.id === firstItem.price.product);
                }
              }

              if (!product && price && price.product) {
                const loadedProduct = this.productsSubject.value.find(p => p.id === price?.product);
                if (loadedProduct) {
                  product = loadedProduct;
                } else {
                  product = {
                    id: price.product || subscriptionData.product?.id || 'unknown',
                    name: subscriptionData.product?.name || 'Subscription Plan',
                    description: subscriptionData.product?.description || '',
                    active: true,
                    prices: []
                  };
                }
              } else if (!product && subscriptionData.product) {
                const loadedProduct = this.productsSubject.value.find(p => p.id === subscriptionData.product.id);
                if (loadedProduct) {
                  product = loadedProduct;
                } else {
                  product = {
                    id: subscriptionData.product.id || 'unknown',
                    name: subscriptionData.product.name || 'Subscription Plan',
                    description: subscriptionData.product.description || '',
                    active: true,
                    prices: []
                  };
                }
              }

              if (!price && subscriptionData.price) {
                price = subscriptionData.price;
              }

              if (product) {
                subscriptions.push({
                  ...subscriptionData,
                  product,
                  price: price || {
                    id: 'unknown',
                    product: product.id,
                    active: true,
                    currency: 'usd',
                    type: 'recurring',
                    unit_amount: 0,
                    recurring: { interval: 'month', interval_count: 1 }
                  }
                });
              } else {
                console.warn('Could not find or create product for subscription:', subscriptionData);
              }
            });

            observer.next(subscriptions);
          }, (error) => {
            console.error('Error listening to subscriptions:', error);
            observer.error(error);
          });
        });

      return () => {
        productsSubscription.unsubscribe();
        snapshotUnsubscribe?.();
      };
    });
  }

  /**
   * Create a billing portal session using Cloud Functions
   * Following the official Firebase Extensions for Stripe Payments pattern
   */
  async createPortalSession(
    currentUser: any,
    returnUrl?: string
  ): Promise<string> {
    if (!currentUser) {
      throw new Error('User must be authenticated to create portal session');
    }

    try {
      const { data } = await this.createPortalLinkCallable({
        returnUrl: returnUrl || window.location.origin,
        locale: "auto", // Optional, defaults to "auto"
        // configuration: "bpc_1JSEAKHYgolSBA358VNoc2Hs", // Optional ID of a portal configuration
      }) as { data: { url: string } };

      return data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Format subscription status for display
   */
  formatSubscriptionStatus(subscription: StripeSubscription): string {
    if (subscription.cancel_at_period_end) {
      return 'Cancels at period end';
    }
    
    switch (subscription.status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past due';
      case 'canceled':
        return 'Canceled';
      case 'incomplete':
        return 'Incomplete';
      case 'unpaid':
        return 'Unpaid';
      default:
        return subscription.status;
    }
  }

  /**
   * Get subscription status color for UI
   */
  getSubscriptionStatusColor(subscription: StripeSubscription): string {
    if (subscription.cancel_at_period_end) {
      return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
    }
    
    switch (subscription.status) {
      case 'active':
      case 'trialing':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      case 'past_due':
      case 'incomplete':
      case 'unpaid':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30';
      case 'canceled':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30';
    }
  }

  /**
   * Format date for display
   */
  formatDate(timestamp: any): string {
    if (!timestamp) {
      return 'N/A';
    }
    
    try {
      let date: Date;
      
      // Handle Firestore Timestamp objects
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
        date = timestamp.toDate();
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Handle Firestore Timestamp with seconds property
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Convert to number if it's a string
      if (typeof timestamp === 'string') {
        timestamp = parseInt(timestamp);
      }
      
      // Only proceed if we have a number
      if (typeof timestamp !== 'number') {
        console.error('Timestamp is not a number:', timestamp);
        return `Invalid timestamp type: ${typeof timestamp}`;
      }
      
      // Try multiple approaches to handle different timestamp formats
      
      // Approach 1: Direct conversion (if already in milliseconds)
      date = new Date(timestamp);
      
      // Check if the result is reasonable
      let year = date.getFullYear();
      if (year >= 1970 && year <= 2100 && !isNaN(year)) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Approach 2: Assume timestamp is in seconds, convert to milliseconds
      date = new Date(timestamp * 1000);
      
      year = date.getFullYear();
      if (year >= 1970 && year <= 2100 && !isNaN(year)) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // Approach 3: Maybe timestamp is in milliseconds but needs division
      date = new Date(timestamp / 1000);
      
      year = date.getFullYear();
      if (year >= 1970 && year <= 2100 && !isNaN(year)) {
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
      
      // If none of the approaches worked, return the raw timestamp info
      console.error('All date conversion approaches failed for timestamp:', timestamp);
      return `Invalid date (${timestamp})`;
      
    } catch (error) {
      console.error('Error in formatDate:', error, 'for timestamp:', timestamp);
      return `Error: ${timestamp}`;
    }
  }
}
